import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { AssistantWorkspace } from "@/components/supervision/AssistantWorkspace";
import { SupervisorWorkspace } from "@/components/supervision/SupervisorWorkspace";
import { api, formatApiError } from "@/lib/api";

export const OBSERVATION_TYPES = ["In-person observation", "Live video observation", "Session review", "Case consultation", "Documentation review", "Other"];


export const formatMinutes = (minutes) => {
  const value = Number(minutes || 0);
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return hours ? `${hours}h${rest ? ` ${rest}m` : ""}` : `${rest}m`;
};

export const monthLabel = (month) => (month ? new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "");

export function SupervisionTotals({ pairId, totals, testId }) {
  const months = totals?.[pairId] || [];
  if (!months.length) return <p className="supervision-totals-empty" data-testid={`${testId}-empty`}>No supervision minutes logged yet.</p>;
  return (
    <table className="supervision-totals" data-testid={testId}>
      <thead><tr><th>Month</th><th>Direct</th><th>Indirect</th><th>Total</th><th>Verified</th></tr></thead>
      <tbody>
        {months.map((row) => (
          <tr key={row.month} data-testid={`${testId}-${row.month}`}>
            <td>{monthLabel(row.month)}</td>
            <td>{formatMinutes(row.direct_minutes)}</td>
            <td>{formatMinutes(row.indirect_minutes)}</td>
            <td><strong>{formatMinutes(row.total_minutes)}</strong></td>
            <td>{formatMinutes(row.verified_minutes)} · {row.note_count} note{row.note_count === 1 ? "" : "s"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SupervisionNotesPanel({ queue, onReload }) {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setOverview((await api.get("/supervision/overview")).data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, queue]);

  async function reload() {
    await load();
    await onReload();
  }

  if (error) return <p className="form-message error-message" data-testid="supervision-overview-error">{error}</p>;
  if (!overview) return null;
  const isAssistantProfile = /assistant|cota|slpa|\bota\b/i.test(queue.provider.discipline || "");
  if (!overview.pairings.length && !overview.services_paused && !overview.setup_in_progress && !isAssistantProfile) {
    return <section className="provider-card" data-testid="supervision-empty"><p className="eyebrow">SUPERVISION</p><strong>No supervision pairing yet</strong><span className="muted">TAG will pair you with a supervisor or assistant and it will show here.</span></section>;
  }

  return (
    <section className="supervision-notes-panel" data-testid="supervision-notes-panel">
      <div className="provider-queue-heading"><ShieldCheck size={18} /><div><h2>Clinical supervision</h2><p>TAG Clinical Supervision Guidebook · DC & MD · OT & SLP. Supervision data is visible only to the pairing, HR and the System Administrator.</p></div></div>
      {overview.setup_in_progress && !overview.services_paused && (
        <p className="form-message services-paused-banner" data-testid="supervision-setup-banner">Supervision setup in progress: your supervision pairing is being finalized by TAG.</p>
      )}
      {overview.services_paused && (
        <p className="form-message error-message services-paused-banner" data-testid="services-paused-banner">Services paused: no active supervisor{overview.services_paused_since ? ` (since ${overview.services_paused_since})` : ""}. Contact TAG before delivering services.</p>
      )}
      {overview.pairings.map((pairing) => (
        <div key={pairing.id} className="supervision-pairing-card">
          {pairing.role === "supervisor" && <SupervisorWorkspace pairing={pairing} overview={overview} onReload={reload} />}
          {pairing.role === "assistant" && <AssistantWorkspace pairing={pairing} overview={overview} onReload={reload} />}
          {pairing.role === "backup" && <p className="pairing-meta" data-testid={`backup-pairing-${pairing.id}`}>You are the backup supervisor for {pairing.supervisor_name} → {pairing.assistant_name} ({pairing.jurisdiction} {pairing.discipline}).</p>}
          <SupervisionTotals pairId={pairing.id} totals={queue.supervision_totals} testId={`supervision-totals-${pairing.id}`} />
        </div>
      ))}
      {!overview.pairings.length && <p className="provider-empty-state" data-testid="supervision-no-pairings">No supervision pairing yet</p>}
    </section>
  );
}
